import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Eye, CheckCircle, AlertCircle, ExternalLink } from "lucide-react";
import { type Job, type User as UserType } from "@/lib/schema";
import { AdminReferralWithUI } from "@/api/referrals";
import { formatDateForSouthAfrica } from "@/lib/textUtils";

interface ReferralViewerData {
  job: Job | null;
  referrer: UserType | null;
  candidateName: string;
  candidateSkills: string[];
  jobSkills: string[];
  matchingSkills: string[];
  missingSkills: string[];
}

interface CVReviewTabProps {
  referral: AdminReferralWithUI;
  viewerData: ReferralViewerData | null;
  isLoadingData: boolean;
  isDownloading: boolean;
  onDownloadCV: () => void;
  onFullCVView: () => void;
}

export function CVReviewTab({ 
  referral, 
  viewerData, 
  isLoadingData, 
  isDownloading, 
  onDownloadCV, 
  onFullCVView 
}: CVReviewTabProps) {
  const candidate = referral.candidateData;
  
  // Get resume information from candidate document with fallback to referral fields
  const latestResume = candidate?.resumes?.[0]; // Get the most recent resume
  const cvUrl = latestResume?.url || referral.candidateResumeUrl;
  const cvFileName = latestResume?.fileName || referral.candidateResumeFileName;
  const cvUploadedAt = latestResume?.uploadedAt;

  return (
    <div className="space-y-6">
      {/* Candidate Information Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="space-grotesk text-lg">Candidate Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Full Name</label>
              <p className="text-sm font-medium text-gray-900 mt-1">
                {candidate ? `${candidate.firstName} ${candidate.lastName}` : 
                 referral.candidateFirstName ? `${referral.candidateFirstName} ${referral.candidateLastName}` :
                 viewerData?.candidateName || 'Loading...'}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</label>
              <p className="text-sm font-medium text-gray-900 mt-1">
                {referral.candidateEmail}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide ">Phone</label>
              <p className="text-sm font-medium text-gray-900  mt-1">
                {candidate?.phoneNumber || referral.candidatePhone || 'Not provided'}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">LinkedIn</label>
              {(candidate?.linkedinUrl || referral.candidateLinkedin) ? (
                <div className="mt-1">
                  <a 
                    href={candidate?.linkedinUrl || referral.candidateLinkedin} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-blue-600 space-grotesk hover:text-blue-700 inline-flex items-center gap-1"
                  >
                    <ExternalLink className="h-3 w-3" />
                    View LinkedIn Profile
                  </a>
                </div>
              ) : (
                <p className="text-sm font-medium text-gray-900 space-grotesk mt-1">
                  Not provided
                </p>
              )}
            </div>
            {candidate?.experienceYears && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Experience</label>
                <p className="text-sm font-medium text-gray-900 mt-1">
                  {candidate.experienceYears} years
                </p>
              </div>
            )}
            {candidate?.resumes && candidate.resumes.length > 0 && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Resumes</label>
                <p className="text-sm font-medium text-gray-900 mt-1">
                  {candidate.resumes.length} resume{candidate.resumes.length > 1 ? 's' : ''} uploaded
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="space-grotesk text-lg">CV Document</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-100 rounded-lg p-8 text-center">
              <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <div className="text-sm text-gray-600 mb-4">
                {cvUrl ? (
                  <div className="text-left">
                    <p><strong>File:</strong> {cvFileName || 'Resume.pdf'}</p>
                    {cvUploadedAt && (
                      <p><strong>Uploaded:</strong> {formatDateForSouthAfrica(cvUploadedAt)}</p>
                    )}
                    <p><strong>Status:</strong> <span className="text-green-600">Available</span></p>
                  </div>
                ) : (
                  <p>No CV document available</p>
                )}
              </div>
              <div className="flex gap-2 justify-center">
                <Button 
                  variant="secondary" 
                  onClick={onFullCVView}
                  disabled={!cvUrl}
                  className="space-grotesk"
                  data-testid="button-open-full-cv"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Open Full View
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={onDownloadCV}
                  disabled={isDownloading || !cvUrl}
                  className="space-grotesk"
                  data-testid="button-open-cv-tab"
                >
                  <ExternalLink className={`h-4 w-4 mr-2 ${isDownloading ? 'animate-spin' : ''}`} />
                  {isDownloading ? 'Opening...' : 'Open CV'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="space-grotesk text-lg">Job Requirements Comparison</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Matching Skills</h4>
              <div className="space-y-2">
                {isLoadingData ? (
                  <span className="text-sm text-gray-500 space-grotesk">Loading...</span>
                ) : viewerData?.matchingSkills.length ? (
                  viewerData.matchingSkills.map((skill, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-gray-700 space-grotesk">{skill}</span>
                    </div>
                  ))
                ) : (
                  <span className="text-sm text-gray-500">No matching skills identified</span>
                )}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Missing Skills</h4>
              <div className="space-y-2">
                {isLoadingData ? (
                  <span className="text-sm text-gray-500 space-grotesk">Loading...</span>
                ) : viewerData?.missingSkills.length ? (
                  viewerData.missingSkills.map((skill, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-orange-600" />
                      <span className="text-sm text-gray-700 space-grotesk">{skill}</span>
                    </div>
                  ))
                ) : (
                  <span className="text-sm text-green-600 space-grotesk">All required skills present</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
